import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Sparkles, Building2, ShieldCheck, BellRing, CheckCircle2, ChevronLeft, ChevronRight, Plus, Trash2, Check } from "lucide-react";

const STEPS = [
  { id: 1, label: "First Site",     icon: Building2 },
  { id: 2, label: "Modules",        icon: ShieldCheck },
  { id: 3, label: "Alert Contacts", icon: BellRing },
  { id: 4, label: "Review",         icon: CheckCircle2 },
];

const BUILDING_TYPES = ["Commercial", "Residential", "Industrial", "Hospital", "Hotel", "Mall", "IT Park"];

const MODULES = [
  { key: "hydrant_system",    label: "Hydrant" },
  { key: "sprinkler_system",  label: "Sprinkler" },
  { key: "booster_pump",      label: "Booster Pump" },
  { key: "detection_system",  label: "Fire Alarm" },
  { key: "lifts_module",      label: "Lift" },
  { key: "ventilation_system",label: "Ventilation" },
  { key: "gateway",           label: "Gateway" },
];

export default function FirstRunWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState({
    site:    { name: "", city: "", building_type: "Commercial", building_height: 18 },
    modules: ["hydrant_system", "detection_system", "gateway"],
    contacts: [{ name: "", mobile: "", email: "" }],
  });

  const valid = {
    1: d.site.name && d.site.city,
    2: d.modules.length > 0,
    3: d.contacts.every((c) => c.name && (c.mobile || c.email)),
    4: true,
  };

  const next = () => {
    if (!valid[step]) { toast.error("Please fill all required fields"); return; }
    setStep(step + 1);
  };
  const back = () => setStep(Math.max(1, step - 1));

  const toggle = (k) => setD({ ...d, modules: d.modules.includes(k) ? d.modules.filter((m) => m !== k) : [...d.modules, k] });
  const addContact = () => d.contacts.length < 3 && setD({ ...d, contacts: [...d.contacts, { name: "", mobile: "", email: "" }] });
  const updContact = (i, patch) => { const c = [...d.contacts]; c[i] = { ...c[i], ...patch }; setD({ ...d, contacts: c }); };
  const delContact = (i) => setD({ ...d, contacts: d.contacts.filter((_, idx) => idx !== i) });

  const finish = async () => {
    setBusy(true);
    try {
      // Save onboarding bundle (re-uses existing endpoint)
      await api.post("/tenant/org/onboarding", {
        first_site: {
          name: d.site.name, city: d.site.city,
          building_type: d.site.building_type, building_height: Number(d.site.building_height) || 0,
        },
        modules: d.modules,
        invitations: d.contacts.filter((c) => c.email).map((c) => ({ email: c.email, role: "viewer" })),
      });
      // Enable selected modules
      await Promise.all(d.modules.map((mk) =>
        api.put(`/billing/modules/${mk}`, { is_enabled: true }).catch(() => {})));
      // Mark wizard completed
      await api.post("/auth/wizard-completed");
      toast.success("Setup complete! Welcome to Fire360Pro");
      nav("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };

  const pct = Math.round((step / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-slate-950 text-gray-900" data-testid="first-run-wizard">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-7 h-7 text-orange-400" strokeWidth={2.5} />
          <h1 className="text-3xl font-extrabold">Welcome — Quick Setup</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">Get your ACMS environment running in 4 minutes</p>

        {/* Progress */}
        <div className="mb-7">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div key={s.id} className="flex flex-col items-center flex-1" data-testid={`fr-step-${s.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${done ? "bg-emerald-500 text-white" : active ? "f360-gradient text-white" : "bg-gray-100 text-slate-400"}`}>
                    {done ? <Check className="w-4 h-4" strokeWidth={2.5} /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mt-1.5 ${active ? "text-orange-700" : done ? "text-emerald-700" : "text-gray-500"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
          <div className="h-2 bg-gray-50 rounded-full overflow-hidden">
            <div className="h-full f360-gradient transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="glass rounded-lg p-6">
          {step === 1 && (
            <div className="space-y-4" data-testid="fr-content-1">
              <h2 className="text-xl font-bold">Add Your First Site</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Site Name *</Label><Input value={d.site.name} onChange={(e) => setD({ ...d, site: { ...d.site, name: e.target.value } })} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Phoenix Marketcity Mall" data-testid="fr-site-name" /></div>
                <div><Label>City *</Label><Input value={d.site.city} onChange={(e) => setD({ ...d, site: { ...d.site, city: e.target.value } })} className="bg-gray-50 border-gray-200 mt-1" placeholder="Mumbai" data-testid="fr-site-city" /></div>
                <div><Label>Building Type</Label>
                  <Select value={d.site.building_type} onValueChange={(v) => setD({ ...d, site: { ...d.site, building_type: v } })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{BUILDING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Building Height (metres)</Label><Input type="number" min={0} step={0.5} value={d.site.building_height} onChange={(e) => setD({ ...d, site: { ...d.site, building_height: e.target.value } })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="fr-content-2">
              <h2 className="text-xl font-bold">Select Modules to Activate</h2>
              <p className="text-xs text-gray-500">You can enable or disable any module later in Billing → Subscription.</p>
              <div className="grid grid-cols-2 gap-3">
                {MODULES.map((m) => {
                  const active = d.modules.includes(m.key);
                  return (
                    <button key={m.key} onClick={() => toggle(m.key)}
                      className={`p-4 rounded-lg border text-left transition-all ${active ? "border-orange-500/60 bg-orange-500/10" : "border-gray-200 bg-gray-50 hover:border-orange-500/30"}`}
                      data-testid={`fr-module-${m.key}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold">{m.label}</div>
                        {active && <Check className="w-5 h-5 text-orange-400" strokeWidth={2.5} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="fr-content-3">
              <h2 className="text-xl font-bold">Alert Contacts</h2>
              <p className="text-xs text-gray-500">Up to 3 contacts will receive critical alerts via SMS &amp; email.</p>
              <div className="space-y-3">
                {d.contacts.map((c, i) => (
                  <div key={i} className="grid grid-cols-7 gap-2 items-center" data-testid={`fr-contact-${i}`}>
                    <Input value={c.name} onChange={(e) => updContact(i, { name: e.target.value })} placeholder="Name *" className="col-span-2 bg-gray-50 border-gray-200" />
                    <Input value={c.mobile} onChange={(e) => updContact(i, { mobile: e.target.value })} placeholder="+91 98XXX XXXXX" className="col-span-2 bg-gray-50 border-gray-200" />
                    <Input value={c.email} onChange={(e) => updContact(i, { email: e.target.value })} placeholder="email@example.com" className="col-span-2 bg-gray-50 border-gray-200" />
                    <button onClick={() => delContact(i)} className="text-red-400 hover:text-red-800" disabled={d.contacts.length === 1}><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                  </div>
                ))}
                {d.contacts.length < 3 && <Button onClick={addContact} variant="outline" className="border-gray-200" data-testid="fr-add-contact"><Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Add Contact</Button>}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="fr-content-4">
              <h2 className="text-xl font-bold">Review &amp; Confirm</h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Site</div><div className="font-semibold">{d.site.name} · {d.site.city} · {d.site.building_type} · {d.site.building_height}m</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Modules ({d.modules.length})</div>
                  <div className="flex flex-wrap gap-1 mt-1">{d.modules.map((m) => <span key={m} className="px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-700 text-[10px] uppercase tracking-widest font-bold">{MODULES.find((x) => x.key === m)?.label || m}</span>)}</div>
                </div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Alert Contacts ({d.contacts.filter((c) => c.name).length})</div>
                  <div className="mt-1 space-y-0.5">{d.contacts.filter((c) => c.name).map((c, i) => <div key={i} className="text-xs">• {c.name} — {c.mobile || c.email}</div>)}</div>
                </div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-sm text-emerald-700">
                ✓ You're ready to go! Click <b>Go to Dashboard</b> to start monitoring.
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-7 pt-5 border-t border-gray-200">
            <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="fr-back">
              <ChevronLeft className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Back
            </Button>
            <div className="text-xs text-gray-500">Step {step} of {STEPS.length}</div>
            {step < STEPS.length ? (
              <Button onClick={next} className="f360-gradient border-0" data-testid="fr-next">Next <ChevronRight className="w-4 h-4 ml-1.5" strokeWidth={2.5} /></Button>
            ) : (
              <Button onClick={finish} disabled={busy} className="f360-gradient border-0" data-testid="fr-finish"><Check className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Go to Dashboard</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
